import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, ArrowLeft, Sparkles, Search, X as XIcon, Mail, Clock, CheckCircle2, ChevronDown, ChevronRight, Users as UsersIcon, FileDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import FileUpload from "@/components/FileUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [groupMetrics, setGroupMetrics] = useState({});

  const groupedTasks = useMemo(() => {
    const byGroup = new Map();
    const singletons = [];
    for (const t of tasks) {
      if (t.group_id) {
        if (!byGroup.has(t.group_id)) byGroup.set(t.group_id, []);
        byGroup.get(t.group_id).push(t);
      } else {
        singletons.push(t);
      }
    }
    const rows = [];
    for (const [gid, members] of byGroup) {
      if (members.length === 1) {
        singletons.push(members[0]);
      } else {
        // Preserve stable display order inside the group (by assignee name).
        members.sort((a, b) => (a.assignee_name || "—").localeCompare(b.assignee_name || "—"));
        rows.push({ kind: "group", gid, members, title: members[0].title, due_date: members[0].due_date });
      }
    }
    for (const t of singletons) rows.push({ kind: "task", task: t });
    return rows;
  }, [tasks]);

  const toggleGroup = (gid) => setExpandedGroups(prev => {
    const next = new Set(prev);
    if (next.has(gid)) next.delete(gid); else next.add(gid);
    return next;
  });

  const statusTally = (members) => {
    const counts = {};
    for (const m of members) counts[m.status] = (counts[m.status] || 0) + 1;
    return Object.entries(counts).map(([s, c]) => `${c} ${s}`).join(" · ");
  };
  const [freelancers, setFreelancers] = useState([]);
  const [allTesters, setAllTesters] = useState([]);
  const [openTask, setOpenTask] = useState(false);
  const [openAssign, setOpenAssign] = useState(false);
  const [openMatch, setOpenMatch] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", test_environment: "", device_requirements: "", payout_amount: 50, allocated_hours: 4, hourly_rate: "", build_files: [], assigned_to: [], status: "Draft" });
  const [members, setMembers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attUploading, setAttUploading] = useState(false);
  const [busyStatus, setBusyStatus] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const attInputRef = useRef(null);

  const changeStatus = async (newStatus) => {
    if (!p || newStatus === p.status) return;
    setBusyStatus(true);
    try {
      await api.patch(`/projects/${id}`, { status: newStatus });
      toast.success(`Project marked ${newStatus}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    } finally { setBusyStatus(false); }
  };

  const loadAttachments = async () => {
    try {
      const { data } = await api.get(`/projects/${id}/attachments`);
      setAttachments(data || []);
    } catch { /* user may not have access */ }
  };

  const onAttachmentPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File exceeds 10MB"); e.target.value = ""; return; }
    setAttUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/projects/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${file.name}`);
      loadAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setAttUploading(false);
      e.target.value = "";
    }
  };

  const deleteAttachment = async (aid, name) => {
    if (!window.confirm(`Remove ${name}? This can't be undone.`)) return;
    try {
      await api.delete(`/projects/${id}/attachments/${aid}`);
      toast.success("Attachment removed");
      loadAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    }
  };

  const fmtBytes = (n) => {
    if (!n) return "0 B";
    const units = ["B","KB","MB","GB"]; let i = 0; let v = n;
    while (v >= 1024 && i < units.length-1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const load = async () => {
    const [pr, ts, mb] = await Promise.all([
      api.get(`/projects/${id}`),
      api.get(`/tasks`, { params: { project_id: id } }),
      api.get(`/projects/${id}/members`).catch(() => ({ data: [] })),
    ]);
    setP(pr.data); setTasks(ts.data || []); setMembers(mb.data || []);
    // Fetch per-group metrics in the background — fire & forget, admins/managers only.
    (async () => {
      if (user.role !== "admin" && user.role !== "manager") return;
      try {
        const { data } = await api.get(`/projects/${id}/task-group-metrics`);
        setGroupMetrics(data || {});
      } catch { /* silent */ }
    })();
    // Build the Testers tab pool = (active legacy assignees) ∪ (Active+Pending project_members)
    const memberRows = mb.data || [];
    const allIds = new Set([
      ...(pr.data.assigned_freelancer_ids || []),
      ...memberRows.filter(m => m.status === "Active" || m.status === "Pending").map(m => m.tester_id),
    ]);
    if (allIds.size > 0) {
      const r = await Promise.all([...allIds].map(fid => api.get(`/users/${fid}`).catch(() => null)));
      // Annotate each row with its membership status (legacy assignees default to Active)
      const memberStatusById = Object.fromEntries(memberRows.map(m => [m.tester_id, m.status]));
      const rows = r.filter(Boolean).map(x => {
        const status = memberStatusById[x.data.id] || "Active";
        return { ...x.data, _membership_status: status };
      });
      // Active first, then Pending; alphabetical within group
      rows.sort((a, b) => {
        const rank = { Active: 0, Pending: 1 };
        return (rank[a._membership_status] ?? 2) - (rank[b._membership_status] ?? 2) || (a.name || "").localeCompare(b.name || "");
      });
      setFreelancers(rows);
    } else {
      setFreelancers([]);
    }
    if (user.role !== "freelancer") {
      const all = await api.get("/users", { params: { role: "freelancer" } });
      setAllTesters(all.data || []);
    }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { loadAttachments(); /* eslint-disable-next-line */ }, [id]);

  if (!p) return <AppLayout title="Loading..."><div>Loading…</div></AppLayout>;

  const createTask = async () => {
    if (!taskForm.title.trim()) { toast.error("Task title is required"); return; }
    const assignees = taskForm.assigned_to || [];
    if (assignees.length === 0) { toast.error("Pick at least one tester"); return; }
    try {
      const base = {
        ...taskForm,
        project_id: id,
        payout_amount: Number(taskForm.payout_amount) || 0,
        allocated_hours: Number(taskForm.allocated_hours) || 0,
      };
      // Only pass hourly_rate if the admin actually entered a number.
      if (taskForm.hourly_rate === "" || taskForm.hourly_rate == null) {
        delete base.hourly_rate;
      } else {
        base.hourly_rate = Number(taskForm.hourly_rate);
      }
      delete base.assigned_to;
      // When >1 tester selected, stamp all clones with a shared group_id so the
      // Task-Group view exists from day one.
      const group_id = assignees.length > 1
        ? (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `grp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`)
        : null;
      // Create one task per selected tester (so each tester has their own work item).
      const results = await Promise.allSettled(
        assignees.map(uid => api.post("/tasks", { ...base, assigned_to: uid, status: "Assigned", ...(group_id ? { group_id } : {}) }))
      );
      const ok = results.filter(r => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok > 0) toast.success(fail === 0 ? `Created ${ok} task${ok > 1 ? "s" : ""}` : `Created ${ok} of ${assignees.length} tasks (${fail} failed)`);
      if (ok === 0) { toast.error("Failed to create tasks"); return; }
      setOpenTask(false);
      setTaskForm({ title: "", description: "", test_environment: "", device_requirements: "", payout_amount: 50, allocated_hours: 4, hourly_rate: "", build_files: [], assigned_to: [], status: "Draft" });
      load();
    } catch { toast.error("Failed"); }
  };

  const saveAssignments = async () => {
    try {
      await api.post(`/projects/${id}/assign`, { freelancer_ids: selected });
      toast.success("Assignments updated");
      setOpenAssign(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const canEdit = user.role === "admin" || (user.role === "manager" && p.manager_id === user.id);

  const runMatcher = async () => {
    setMatchLoading(true);
    setOpenMatch(true);
    try {
      const { data } = await api.get(`/projects/${id}/match-testers`);
      setMatchData(data);
    } catch { toast.error("Match failed"); }
    finally { setMatchLoading(false); }
  };

  const assignFromMatch = async (testerId) => {
    const current = p.assigned_freelancer_ids || [];
    if (current.includes(testerId)) return;
    try {
      await api.post(`/projects/${id}/assign`, { freelancer_ids: [...current, testerId] });
      toast.success("Tester assigned");
      load();
      runMatcher();
    } catch { toast.error("Failed"); }
  };

  const downloadSubmissionReport = async (fmt) => {
    try {
      if (fmt === "json") {
        const { data } = await api.get(`/projects/${id}/submission-report`, { params: { format: "json" } });
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `submission_report_${p.name?.replace(/\s+/g,"_") || "project"}.json`;
        a.click(); URL.revokeObjectURL(url);
      } else {
        const res = await api.get(`/projects/${id}/submission-report`, {
          params: { format: fmt },
          responseType: "blob",
        });
        const ext = fmt === "pdf" ? "pdf" : "csv";
        const mime = fmt === "pdf" ? "application/pdf" : "text/csv";
        const blob = new Blob([res.data], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `submission_report_${p.name?.replace(/\s+/g,"_") || "project"}.${ext}`;
        a.click(); URL.revokeObjectURL(url);
      }
      toast.success(`${fmt.toUpperCase()} report downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate report");
    }
  };

  return (
    <AppLayout title={p.name} subtitle={`Client: ${p.client_name}`}>
      <button onClick={()=>nav("/projects")} className="text-sm text-slate-500 hover:text-slate-900 mb-3 flex items-center gap-1.5" data-testid="btn-back-projects"><ArrowLeft className="w-4 h-4"/> Projects</button>

      <div className="kpi-card mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <StatusBadge kind="task" value={p.status} />
            </div>
            <p className="text-sm text-slate-600 mt-3 max-w-2xl">{p.description}</p>
            <p className="text-xs text-slate-500 mt-2"><b>Scope:</b> {p.test_scope}</p>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>{p.start_date && new Date(p.start_date).toDateString()}</div>
            <div>→ {p.end_date && new Date(p.end_date).toDateString()}</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="tasks">
        <TabsList>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="testers" data-testid="tab-testers">Testers ({freelancers.length})</TabsTrigger>
          {canEdit && (
            <TabsTrigger value="invitations" data-testid="tab-invitations">Invitations ({members.length})</TabsTrigger>
          )}
          <TabsTrigger value="attachments" data-testid="tab-attachments">Attachments ({attachments.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-4">
          <div className="flex justify-end mb-3 gap-2">
            {canEdit && (
              <Dialog open={openTask} onOpenChange={setOpenTask}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-task"><Plus className="w-4 h-4 mr-1.5"/>New task</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Create task</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Title</Label><Input value={taskForm.title} onChange={e=>setTaskForm(f=>({...f, title:e.target.value}))} data-testid="input-task-title"/></div>
                    <div>
                      <Label>Task Instruction</Label>
                      <RichTextEditor
                        value={taskForm.description}
                        onChange={(html) => setTaskForm(f => ({ ...f, description: html }))}
                        placeholder="Detailed instructions for testers — supports headings, bold, lists, code blocks…"
                        minHeight={180}
                        testid="input-task-desc"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Test environment</Label><Input value={taskForm.test_environment} onChange={e=>setTaskForm(f=>({...f, test_environment:e.target.value}))} data-testid="input-env"/></div>
                      <div><Label>Devices</Label><Input value={taskForm.device_requirements} onChange={e=>setTaskForm(f=>({...f, device_requirements:e.target.value}))} data-testid="input-device-reqs"/></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label>Payout ($)</Label><Input type="number" value={taskForm.payout_amount} onChange={e=>setTaskForm(f=>({...f, payout_amount: e.target.value}))} data-testid="input-payout"/></div>
                      <div>
                        <Label>Hours</Label>
                        <Input type="number" min={0} step="0.5" value={taskForm.allocated_hours} onChange={e=>setTaskForm(f=>({...f, allocated_hours: e.target.value}))} data-testid="input-hours"/>
                      </div>
                      <div>
                        <Label>Rate override ($/hr)</Label>
                        <Input type="number" min={0} step="0.5" value={taskForm.hourly_rate} onChange={e=>setTaskForm(f=>({...f, hourly_rate: e.target.value}))} placeholder="Auto" data-testid="input-hourly-rate"/>
                      </div>
                    </div>
                    <div>
                      <Label>Client build (APK / IPA / AAB / ZIP — ≤ 500MB)</Label>
                      <FileUpload
                        testid="upload-build-file"
                        endpoint="/files/upload-build"
                        accept=".apk,.ipa,.aab,.zip"
                        maxMB={500}
                        label="Attach a build for testers to install (optional)"
                        onUploaded={(d) => setTaskForm(f => ({ ...f, build_files: [...(f.build_files || []), d] }))}
                      />
                      {taskForm.build_files?.length > 0 && (
                        <ul className="mt-2 space-y-1" data-testid="task-form-builds">
                          {taskForm.build_files.map((b, idx) => (
                            <li key={idx} className="flex items-center justify-between text-xs border border-slate-100 rounded px-2 py-1">
                              <span className="truncate">{b.name}</span>
                              <button
                                type="button"
                                onClick={() => setTaskForm(f => ({ ...f, build_files: f.build_files.filter((_, i) => i !== idx) }))}
                                className="text-slate-400 hover:text-red-600 ml-2"
                                data-testid={`task-form-build-remove-${idx}`}
                              >×</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <Label>Assign to</Label>
                      <MultiAssigneePicker
                        testers={allTesters}
                        memberIds={p.assigned_freelancer_ids || []}
                        value={taskForm.assigned_to}
                        onChange={(ids) => setTaskForm(f => ({ ...f, assigned_to: ids }))}
                      />
                      <div className="text-[11px] text-slate-500 mt-1">
                        {taskForm.assigned_to.length > 0
                          ? `${taskForm.assigned_to.length} tester${taskForm.assigned_to.length > 1 ? "s" : ""} — one task will be created per tester.`
                          : "Pick one or more approved testers."}
                      </div>
                    </div>
                  </div>
                  <DialogFooter><Button onClick={createTask} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-create-task">Create</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Title</th><th>Status</th><th>Assignee</th><th>Rate</th><th>Payout</th><th>Due</th>
                </tr>
              </thead>
              <tbody>
                {groupedTasks.map(row => {
                  if (row.kind === "task") {
                    const t = row.task;
                    return (
                      <tr key={t.id} className="cursor-pointer" onClick={()=>nav(`/tasks/${t.id}`)} data-testid={`row-task-${t.id}`}>
                        <td><span className="font-mono text-[11px] text-slate-400 mr-2">{t.id.slice(0,6).toUpperCase()}</span>{t.title}</td>
                        <td><StatusBadge kind="task" value={t.status}/></td>
                        <td>{t.assignee_name || "—"}</td>
                        <td className="font-mono text-xs">{t.effective_hourly_rate ? `$${t.effective_hourly_rate.toFixed(2)}/hr` : "—"}</td>
                        <td className="font-mono">${t.payout_amount?.toFixed(2)}</td>
                        <td>{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</td>
                      </tr>
                    );
                  }
                  const open = expandedGroups.has(row.gid);
                  const total = row.members.length;
                  return (
                    <Fragment key={row.gid}>
                      <tr
                        className="cursor-pointer bg-cyan-50/40 hover:bg-cyan-50"
                        onClick={() => toggleGroup(row.gid)}
                        data-testid={`row-task-group-${row.gid}`}
                      >
                        <td>
                          <span className="inline-flex items-center flex-wrap gap-1.5">
                            {open ? <ChevronDown className="w-4 h-4 text-slate-500"/> : <ChevronRight className="w-4 h-4 text-slate-500"/>}
                            <span className="font-mono text-[11px] text-slate-400 mr-1">GRP-{row.gid.slice(0,6).toUpperCase()}</span>
                            <span className="font-medium text-slate-900">{row.title}</span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-cyan-800 bg-cyan-100 px-1.5 py-0.5 rounded border border-cyan-200">
                              <UsersIcon className="w-3 h-3"/> {total} testers
                            </span>
                            {groupMetrics[row.gid] && (
                              <>
                                <span className="inline-flex items-center gap-1 text-[11px] text-rose-800 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200" data-testid={`metric-bugs-${row.gid}`} title="Bugs across the whole group">
                                  🐞 {groupMetrics[row.gid].bug_count} bugs
                                </span>
                                {groupMetrics[row.gid].avg_quality_score != null && (
                                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200" data-testid={`metric-quality-${row.gid}`} title={`Average quality from ${groupMetrics[row.gid].reviewed_count} reviewed submission(s)`}>
                                    ★ {groupMetrics[row.gid].avg_quality_score}/100
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                        </td>
                        <td colSpan={2} className="text-xs text-slate-500">{statusTally(row.members)}</td>
                        <td colSpan={2} className="text-xs text-slate-500">
                          <button
                            type="button"
                            className="text-cyan-700 hover:text-cyan-900 font-medium"
                            onClick={(e) => { e.stopPropagation(); nav(`/task-groups/${row.gid}`); }}
                            data-testid={`btn-open-group-${row.gid}`}
                          >
                            Open task group →
                          </button>
                        </td>
                        <td>{row.due_date ? new Date(row.due_date).toLocaleDateString() : "—"}</td>
                      </tr>
                      {open && row.members.map(t => (
                        <tr key={t.id} className="cursor-pointer bg-white/70 hover:bg-slate-50" onClick={() => nav(`/tasks/${t.id}`)} data-testid={`row-task-${t.id}`}>
                          <td className="pl-10">
                            <span className="font-mono text-[11px] text-slate-400 mr-2">{t.id.slice(0,6).toUpperCase()}</span>
                            <span className="text-slate-700">{t.assignee_name || "—"}</span>
                          </td>
                          <td><StatusBadge kind="task" value={t.status}/></td>
                          <td className="text-slate-500">{t.assignee_name || "—"}</td>
                          <td className="font-mono text-xs">{t.effective_hourly_rate ? `$${t.effective_hourly_rate.toFixed(2)}/hr` : "—"}</td>
                          <td className="font-mono">${t.payout_amount?.toFixed(2)}</td>
                          <td>{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                {groupedTasks.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-500">No tasks yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="testers" className="mt-4">
          <div className="flex justify-end mb-3 gap-2">
            {canEdit && (
              <>
                <Button variant="outline" onClick={runMatcher} data-testid="btn-suggest-testers">
                  <Sparkles className="w-4 h-4 mr-1.5 text-cyan-600"/>Suggest testers (AI)
                </Button>
                <Dialog open={openAssign} onOpenChange={(o)=>{setOpenAssign(o); if(o){ setSelected(p.assigned_freelancer_ids || []); setAssignSearch(""); }}}>
                <DialogTrigger asChild><Button className="bg-slate-900 hover:bg-slate-800" data-testid="btn-assign-testers"><Plus className="w-4 h-4 mr-1.5"/>Assign testers</Button></DialogTrigger>
                <DialogContent className="max-h-[80vh] overflow-auto">
                  <DialogHeader>
                    <DialogTitle>Assign testers</DialogTitle>
                    <DialogDescription>{allTesters.length} testers registered. Approved testers are listed first; pending/suspended testers are shown so you can still pick them.</DialogDescription>
                  </DialogHeader>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                    <Input
                      value={assignSearch}
                      onChange={(e)=>setAssignSearch(e.target.value)}
                      placeholder="Search by name, email, country or skill…"
                      className="pl-9"
                      autoCapitalize="none"
                      autoCorrect="off"
                      data-testid="input-assign-search"
                    />
                  </div>
                  <div className="space-y-2">
                    {(() => {
                      const term = assignSearch.trim().toLowerCase();
                      const sorted = [...allTesters].sort((a, b) => {
                        const rank = { approved: 0, pending: 1, suspended: 2, rejected: 3 };
                        return (rank[a.status] ?? 4) - (rank[b.status] ?? 4) || (a.name || "").localeCompare(b.name || "");
                      });
                      const visible = !term ? sorted : sorted.filter(t =>
                        (t.name || "").toLowerCase().includes(term)
                        || (t.email || "").toLowerCase().includes(term)
                        || (t.country || "").toLowerCase().includes(term)
                        || (t.testing_skills || []).some(s => s.toLowerCase().includes(term))
                      );
                      if (visible.length === 0) {
                        return <div className="text-center text-sm text-slate-500 py-6">{allTesters.length === 0 ? "No registered testers yet." : `No testers match "${assignSearch}"`}</div>;
                      }
                      return visible.map(t => {
                        const isBlocked = t.status === "suspended" || t.status === "rejected";
                        return (
                        <label
                          key={t.id}
                          className={`flex items-center gap-3 p-2 border border-slate-200 rounded-md ${
                            isBlocked ? "opacity-50 cursor-not-allowed bg-slate-50" : "hover:bg-slate-50 cursor-pointer"
                          }`}
                          title={isBlocked ? `${t.status} testers cannot be assigned new work` : undefined}
                          data-testid={`row-tester-${t.id}`}
                        >
                          <Checkbox
                            checked={selected.includes(t.id)}
                            disabled={isBlocked}
                            onCheckedChange={(v)=>{
                              if (isBlocked) return;
                              setSelected(prev => v ? [...prev, t.id] : prev.filter(x=>x!==t.id));
                            }}
                            data-testid={`checkbox-tester-${t.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-sm font-medium text-slate-900 truncate">{t.name}</div>
                              <StatusBadge kind="user" value={t.status}/>
                              {isBlocked && <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600">Not assignable</span>}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{t.email} · {t.country || "—"}{t.testing_skills?.length ? " · " + t.testing_skills.slice(0,3).join(", ") : ""}</div>
                          </div>
                          <div className="text-xs font-mono text-slate-500 flex-shrink-0">{t.tester_score?.toFixed(0) ?? "—"}</div>
                        </label>
                        );
                      });
                    })()}
                  </div>
                  <DialogFooter><Button onClick={saveAssignments} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-save-assignments">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
              </>
            )}
          </div>

          {/* AI Match dialog */}
          <Dialog open={openMatch} onOpenChange={setOpenMatch}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-cyan-600"/> AI-suggested testers
                  {matchData?.llm_used && <span className="pill bg-cyan-50 text-cyan-700 border-cyan-200 ml-1" data-testid="badge-llm-used">Gemini 3 Flash</span>}
                </DialogTitle>
                <DialogDescription>All registered testers ranked by match score. Approved testers appear first; pending or suspended testers are shown lower so admins can still discover them.</DialogDescription>
              </DialogHeader>
              {matchLoading && <div className="py-8 text-center text-slate-500">Computing matches…</div>}
              {!matchLoading && matchData && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {matchData.project_skills.length > 0 && <>
                      <span className="text-slate-500">Skills detected:</span>
                      {matchData.project_skills.map(s => <span key={s} className="pill bg-cyan-50 text-cyan-700 border-cyan-200">{s}</span>)}
                    </>}
                    {matchData.project_domains.length > 0 && <>
                      <span className="text-slate-500 ml-2">Domains:</span>
                      {matchData.project_domains.map(d => <span key={d} className="pill bg-slate-100 text-slate-700 border-slate-200">{d}</span>)}
                    </>}
                  </div>
                  <div className="space-y-2">
                    {matchData.matches.map(m => (
                      <div key={m.id} className={`border border-slate-200 rounded-md p-3 flex items-center gap-3 ${m.status !== "approved" ? "opacity-70" : ""}`} data-testid={`match-${m.id}`}>
                        <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-display font-bold flex-shrink-0">{m.name?.[0] || "?"}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium text-slate-900 truncate">{m.name}</div>
                            <span className="pill bg-slate-900 text-cyan-300 border-cyan-700">{m.match_score}</span>
                            <StatusBadge kind="user" value={m.status}/>
                          </div>
                          <div className="text-xs text-slate-500 truncate">{m.reasons.join(" · ") || "—"}</div>
                        </div>
                        {m.already_assigned ? (
                          <span className="pill bg-emerald-50 text-emerald-700 border-emerald-200 flex-shrink-0">Assigned</span>
                        ) : m.status !== "approved" ? (
                          <span className="text-[11px] text-slate-500 italic flex-shrink-0">Approve first</span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={()=>assignFromMatch(m.id)} data-testid={`btn-assign-match-${m.id}`} className="flex-shrink-0">Assign</Button>
                        )}
                      </div>
                    ))}
                    {matchData.matches.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No approved testers available.</div>}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Status</th><th>Country</th><th>Skills</th><th>Score</th></tr></thead>
              <tbody>
                {freelancers.map(f => (
                  <tr key={f.id} className="cursor-pointer" onClick={()=>nav(`/users/${f.id}`)} data-testid={`row-tester-${f.id}`}>
                    <td><b>{f.name}</b><br/><span className="text-xs text-slate-500">{f.email}</span></td>
                    <td>
                      {f._membership_status === "Pending" ? (
                        <span className="pill bg-amber-50 text-amber-800 border-amber-300 text-xs inline-flex items-center gap-1"><Clock className="w-3 h-3"/>Pending invite</span>
                      ) : (
                        <span className="pill bg-emerald-50 text-emerald-700 border-emerald-300 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>Active</span>
                      )}
                    </td>
                    <td>{f.country || "—"}</td>
                    <td className="text-xs">{f.testing_skills?.slice(0,3).join(", ")}</td>
                    <td className="font-mono">{f.tester_score?.toFixed(0) ?? "—"}</td>
                  </tr>
                ))}
                {freelancers.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500">No testers assigned.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="attachments" className="mt-4">
          <div className="kpi-card" data-testid="attachments-panel">
            <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-4">
              <div>
                <div className="text-sm font-semibold text-slate-900">Project attachments</div>
                <p className="text-xs text-slate-500 mt-0.5">Specs, briefs, NDAs, test plans — up to 10MB each. Stored on Emergent Object Storage.</p>
              </div>
              {canEdit && (
                <div>
                  <input ref={attInputRef} type="file" hidden onChange={onAttachmentPick}
                         accept=".pdf,.json,.csv,.txt,.png,.jpg,.jpeg,.gif,.webp,.docx,.xlsx,.zip,.doc,.xls"
                         data-testid="input-attachment-file" />
                  <Button onClick={() => attInputRef.current?.click()} disabled={attUploading}
                          className="bg-slate-900 hover:bg-slate-800" data-testid="btn-upload-attachment">
                    {attUploading ? "Uploading…" : (<><Plus className="w-4 h-4 mr-1.5"/>Add attachment</>)}
                  </Button>
                </div>
              )}
            </div>
            {attachments.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-8 border border-dashed border-slate-200 rounded">No attachments yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="attachments-list">
                {attachments.map(a => (
                  <li key={a.id} className="flex items-center justify-between py-3 gap-3" data-testid={`attachment-row-${a.id}`}>
                    <div className="min-w-0 flex-1">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.file_id}`}
                         target="_blank" rel="noreferrer"
                         className="text-sm font-medium text-slate-900 hover:text-cyan-700 truncate block"
                         data-testid={`attachment-link-${a.id}`}>{a.name}</a>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {fmtBytes(a.size)} · {a.mime?.split("/").pop() || "file"} · {a.uploader_name || "—"} · {new Date(a.uploaded_at).toLocaleString()}
                      </div>
                    </div>
                    {canEdit && (a.uploader_id === user.id || user.role === "admin" || p.manager_id === user.id) && (
                      <button onClick={() => deleteAttachment(a.id, a.name)}
                              className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                              data-testid={`btn-delete-attachment-${a.id}`}>
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4">
          <div className="space-y-4">
            {canEdit && (
              <div className="kpi-card" data-testid="settings-status-card">
                <h3 className="font-display font-bold text-slate-900 mb-1">Project status</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Move the project through its lifecycle. Draft projects are hidden from testers; only Active projects can be worked on.
                </p>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Current status</div>
                <div className="mb-4"><StatusBadge kind="task" value={p.status} /></div>

                <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Change status to</div>
                <div className="flex flex-wrap gap-2" data-testid="status-transition-buttons">
                  {["draft","active","paused","completed","archived"].map(s => (
                    <Button
                      key={s}
                      variant={p.status === s ? "default" : "outline"}
                      disabled={busyStatus || p.status === s}
                      onClick={() => changeStatus(s)}
                      className={p.status === s ? "bg-slate-900 hover:bg-slate-900" : ""}
                      data-testid={`btn-status-${s}`}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-3 leading-snug">
                  <b>Draft</b> → not visible to testers · <b>Active</b> → open for testing · <b>Paused</b> → temporarily on hold · <b>Completed</b> → wrapped up · <b>Archived</b> → hidden from lists.
                </p>
              </div>
            )}
            {canEdit && (
              <ProjectHourlyRateCard project={p} onSaved={load} />
            )}
            <div className="kpi-card">
              <div className="text-sm text-slate-700">Project ID: <span className="font-mono">{p.id}</span></div>
              <div className="text-sm text-slate-700 mt-2">Created: {new Date(p.created_at).toLocaleString()}</div>
            </div>
          </div>
        </TabsContent>

        {canEdit && (
          <TabsContent value="invitations" className="mt-4">
            <InvitationsPanel projectId={id} members={members} onChange={load} />
          </TabsContent>
        )}
      </Tabs>
    </AppLayout>
  );
}

/* ---------- Invitations Panel (admin/manager) ---------- */
function InvitationsPanel({ projectId, members, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(null);
  const boxRef = useRef(null);
  const searchTimerRef = useRef(null);

  const runSearch = async (term) => {
    setSearching(true);
    try {
      const { data } = await api.get("/users", { params: { role: "freelancer", q: term, status: "approved" } });
      setResults((data || []).slice(0, 8));
    } catch { /* swallow */ } finally { setSearching(false); }
  };

  const onQueryChange = (val) => {
    setQ(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const term = val.trim();
    if (term.length < 2) { setResults([]); setSearching(false); return; }
    searchTimerRef.current = setTimeout(() => runSearch(term), 250);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!results.length) return;
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setResults([]); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [results.length]);

  const invitedIds = useMemo(() => new Set(members.filter(m => m.status === "Pending" || m.status === "Active").map(m => m.tester_id)), [members]);

  const invite = async (tester) => {
    setBusy(tester.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/invite-tester`, { tester_id: tester.id });
      if (data.already_member) {
        toast.info(`Already ${data.status} on this project`);
      } else {
        toast.success(`Invited ${tester.name}`);
      }
      setQ(""); setResults([]); setSearching(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send invitation");
    } finally { setBusy(null); }
  };

  const cancel = async (member) => {
    setBusy(member.id);
    try {
      await api.delete(`/projects/${projectId}/members/${member.id}`);
      toast.success("Invitation cancelled");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="invitations-panel">
      <div className="kpi-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-cyan-400 flex items-center justify-center"><Mail className="w-4 h-4" strokeWidth={1.75}/></div>
          <div>
            <h3 className="font-display font-bold text-slate-900">Invite testers</h3>
            <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">Search approved testers by name or email</div>
          </div>
        </div>

        <div className="relative" ref={boxRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input
            value={q}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Type at least 2 characters of name or email…"
            className="pl-9"
            autoCapitalize="none"
            autoCorrect="off"
            data-testid="input-invite-search"
          />
          {(searching || results.length > 0) && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-auto" data-testid="invite-results">
              {searching && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
              {!searching && results.length === 0 && q.trim().length >= 2 && (
                <div className="px-3 py-3 text-sm text-slate-500">No approved testers match &ldquo;{q}&rdquo;</div>
              )}
              {!searching && results.map(r => {
                const already = invitedIds.has(r.id);
                return (
                  <div key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0" data-testid={`invite-result-${r.id}`}>
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-medium text-slate-700 text-sm flex-shrink-0">
                      {r.name?.[0] || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{r.name}</div>
                      <div className="text-xs text-slate-500 truncate">{r.email} · {r.country || "—"}</div>
                    </div>
                    {r.tester_score != null && (
                      <div className="text-xs font-mono text-slate-500">{Math.round(r.tester_score)}</div>
                    )}
                    {already ? (
                      <span className="pill bg-slate-100 text-slate-600 border-slate-200 text-xs">Already member</span>
                    ) : (
                      <Button size="sm" disabled={busy === r.id} onClick={() => invite(r)} className="bg-cyan-600 hover:bg-cyan-700" data-testid={`btn-invite-${r.id}`}>
                        Invite
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Invited testers table */}
      <div className="kpi-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-slate-900">Invited testers</h3>
          <div className="text-xs text-slate-500">{members.length} total</div>
        </div>

        {members.length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-500" data-testid="invitations-empty">
            No invitations yet — search above to invite testers.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-hidden">
              <table className="data-table">
                <thead><tr><th>Tester</th><th>Email</th><th>Country</th><th>Status</th><th>Invited</th><th></th></tr></thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.id} data-testid={`row-member-${m.id}`}>
                      <td className="font-medium text-slate-900">{m.tester_name || "—"}</td>
                      <td className="text-slate-600 text-xs">{m.tester_email || "—"}</td>
                      <td className="text-slate-600 text-xs">{m.tester_country || "—"}</td>
                      <td><MemberStatusBadge status={m.status}/></td>
                      <td className="text-xs text-slate-500">{m.invited_at ? new Date(m.invited_at).toLocaleDateString() : "—"}</td>
                      <td>
                        {m.status === "Pending" && (
                          <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => cancel(m)} data-testid={`btn-cancel-${m.id}`}>
                            <XIcon className="w-3.5 h-3.5 mr-1"/>Cancel
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2.5" data-testid="invitations-mobile">
              {members.map(m => (
                <div key={m.id} className="border border-slate-200 rounded-md p-3" data-testid={`card-member-${m.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{m.tester_name || "—"}</div>
                      <div className="text-xs text-slate-500 truncate">{m.tester_email || "—"}</div>
                    </div>
                    <MemberStatusBadge status={m.status}/>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{m.tester_country || "—"} · invited {m.invited_at ? new Date(m.invited_at).toLocaleDateString() : "—"}</span>
                    {m.status === "Pending" && (
                      <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => cancel(m)} data-testid={`mbtn-cancel-${m.id}`}><XIcon className="w-3.5 h-3.5 mr-1"/>Cancel</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------- Multi-Assignee Picker (approved testers only) ---------- */
function MultiAssigneePicker({ testers, memberIds, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const approved = useMemo(() => {
    const memberSet = new Set(memberIds || []);
    return (testers || [])
      .filter(t => t.status === "approved")
      .sort((a, b) => {
        const m = (memberSet.has(b.id) ? 1 : 0) - (memberSet.has(a.id) ? 1 : 0);
        if (m !== 0) return m;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [testers, memberIds]);

  const term = q.trim().toLowerCase();
  const visible = !term ? approved : approved.filter(t =>
    (t.name || "").toLowerCase().includes(term) || (t.email || "").toLowerCase().includes(term)
  );

  const toggle = (id) => {
    const set = new Set(value || []);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange([...set]);
  };

  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const summary = (() => {
    if (!value || value.length === 0) return `Select approved testers (${approved.length} available)`;
    if (value.length === 1) {
      const t = approved.find(x => x.id === value[0]);
      return t?.name || "1 selected";
    }
    return `${value.length} testers selected`;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white text-left flex items-center justify-between text-sm hover:border-slate-400 transition-colors min-h-[44px]"
          data-testid="select-assignee"
        >
          <span className={value.length === 0 ? "text-slate-500" : "text-slate-900 truncate"}>{summary}</span>
          <span className="flex items-center gap-1 flex-shrink-0 ml-2">
            {value.length > 0 && (
              <span
                onClick={clear}
                role="button"
                tabIndex={0}
                className="text-slate-400 hover:text-slate-700 p-1 -m-1 rounded"
                data-testid="btn-clear-assignees"
                aria-label="Clear all selected testers"
              >
                <XIcon className="w-3.5 h-3.5"/>
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-slate-500"/>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-80 overflow-hidden flex flex-col"
        data-testid="assignee-popover"
      >
        <div className="p-2 border-b border-slate-200">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"/>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search approved testers…"
              className="pl-8 h-9 text-sm"
              autoCapitalize="none"
              autoCorrect="off"
              data-testid="input-assignee-search"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1" data-testid="assignee-options">
          {visible.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-slate-500">
              {approved.length === 0 ? "No approved testers yet." : "No matches."}
            </div>
          ) : (
            visible.map(u => {
              const isMember = (memberIds || []).includes(u.id);
              const checked = (value || []).includes(u.id);
              return (
                <label
                  key={u.id}
                  className={`flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 ${checked ? "bg-cyan-50/40" : ""}`}
                  data-testid={`option-assignee-${u.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(u.id)}
                    data-testid={`checkbox-assignee-${u.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                      {u.name}
                      {isMember && <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-medium flex-shrink-0">Member</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{u.email} · {u.country || "—"}</div>
                  </div>
                  {u.tester_score != null && (
                    <div className="text-[11px] font-mono text-slate-500 flex-shrink-0">{Math.round(u.tester_score)}</div>
                  )}
                </label>
              );
            })
          )}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t border-slate-200 flex items-center justify-between">
            <span className="text-xs text-slate-500">{value.length} selected</span>
            <Button size="sm" onClick={() => setOpen(false)} className="h-7 bg-slate-900 hover:bg-slate-800 text-xs" data-testid="btn-assignees-done">
              Done
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


function MemberStatusBadge({ status }) {
  const styles = {
    Pending:   { cls: "bg-amber-50 text-amber-800 border-amber-300",       icon: Clock },
    Active:    { cls: "bg-emerald-50 text-emerald-700 border-emerald-300", icon: CheckCircle2 },
    Declined:  { cls: "bg-red-50 text-red-700 border-red-200",             icon: XIcon },
    Cancelled: { cls: "bg-slate-100 text-slate-600 border-slate-300",      icon: XIcon },
  };
  const s = styles[status] || styles.Cancelled;
  const Icon = s.icon;
  return (
    <span className={`pill border ${s.cls} text-xs inline-flex items-center gap-1`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3"/>{status}
    </span>
  );
}

function ProjectHourlyRateCard({ project, onSaved }) {
  const [rate, setRate] = useState(
    project.default_hourly_rate == null || project.default_hourly_rate === "" ? "" : String(project.default_hourly_rate)
  );
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const n = rate === "" ? null : Number(rate);
    if (n !== null && (Number.isNaN(n) || n < 0 || n > 1000)) {
      toast.error("Rate must be between 0 and 1000");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { default_hourly_rate: n });
      toast.success(n == null ? "Project rate cleared" : `Project rate set to $${n}/hr`);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rate");
    } finally { setBusy(false); }
  };
  return (
    <div className="kpi-card" data-testid="project-rate-card">
      <h3 className="font-display font-bold text-slate-900 mb-1">Project hourly rate</h3>
      <p className="text-sm text-slate-500 mb-4">
        Overrides each tester&apos;s default rate for tasks in this project. Leave empty to use the tester&apos;s own default. Individual tasks can still override this value.
      </p>
      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-sm">$</span>
        <Input
          type="number"
          min={0}
          max={1000}
          step="0.5"
          value={rate}
          onChange={(e)=>setRate(e.target.value)}
          className="max-w-[140px]"
          placeholder="Use tester default"
          inputMode="decimal"
          data-testid="input-project-hourly-rate"
        />
        <span className="text-slate-500 text-sm">/ hr</span>
        <Button onClick={save} disabled={busy} className="ml-auto bg-cyan-600 hover:bg-cyan-700" data-testid="btn-save-project-rate">Save</Button>
      </div>
    </div>
  );
}
